// File: src/pages/AppointmentForm.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import options from "./options.json";
import { DateTime } from "luxon";

const AppointmentForm = () => {
  const { user } = useAuth();
  const hasPhone = !!(user?.phoneNumber);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    times: [options[0].value],
    autoBook: true,
    minSpots: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      const fetchAppointment = async () => {
        try {
          setLoading(true);
          const res = await api.get(`/api/appointments/${id}`);
          const { times } = res.data;

          setFormData({
            date: DateTime.fromISO(times[0])
              .setZone("America/Chicago")
              .toFormat("yyyy-MM-dd"),
            times: times.map((t) =>
              DateTime.fromISO(t).setZone("America/Chicago").toFormat("HH:mm")
            ),
            autoBook: res.data.autoBook !== false,
            minSpots: res.data.minSpots || 1,
          });
        } catch (err) {
          const errorMessage =
            err.response?.data?.message ||
            "Failed to fetch appointment details";
          setError(errorMessage);
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      fetchAppointment();
    }
  }, [id, isEditMode]);

  const onChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    // Clear error when user makes changes
    setError("");
  };

  const onTimeChange = (index, value) => {
    const updatedTimes = [...formData.times];
    updatedTimes[index] = value;
    setFormData({ ...formData, times: updatedTimes });
  };

  const addTimeSlot = () => {
    const lastTimesValue = formData.times[formData.times.length - 1];

    setFormData({
      ...formData,
      times: [
        ...formData.times,
        options[options.findIndex((o) => o.value === lastTimesValue) + 1]
          ?.value ||
          options.find((o) => !formData.times.includes(o.value)).value,
      ],
    });
  };

  const removeTimeSlot = (index) => {
    const updatedTimes = formData.times.filter((_, i) => i !== index);
    setFormData({ ...formData, times: updatedTimes });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Filter out empty time slots
      const filteredTimes = formData.times.filter((time) => time.trim() !== "");

      if (filteredTimes.length === 0) {
        setError("Please add at least one time slot");
        setLoading(false);
        return;
      }

      const appointmentData = {
        ...formData,
        times: filteredTimes.map((t) =>
          DateTime.fromISO(`${formData.date}T${t}`, {
            zone: "America/Chicago",
          }).toISO()
        ),
      };

      if (isEditMode) {
        await api.put(`/api/appointments/${id}`, appointmentData);
      } else {
        await api.post("/api/appointments", appointmentData);
      }

      navigate("/dashboard");
    } catch (err) {
      setError("Failed to save appointment");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="appointment-form-container">
      <h1>{isEditMode ? "Edit Appointment" : "Create New Appointment"}</h1>
      {error && (
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="form-group">
          <label htmlFor="date">Date</label>
          <input
            type="date"
            id="date"
            name="date"
            min={new Date().toISOString().slice(0, 10)}
            max={DateTime.now()
              .setZone("America/Chicago")
              .plus({ week: 1 })
              .toFormat("yyyy-MM-dd")}
            value={formData.date}
            onChange={onChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Time Slots</label>
          <p>
            <small>
              Time slots to look for in order of preference. Only one time will
              be booked.
            </small>
          </p>
          {formData.times.map((time, index) => (
            <div key={index} className="time-slot">
              <select
                id="timeSelect"
                name="timeSelect"
                value={time}
                required
                onChange={(e) => onTimeChange(index, e.target.value)}
              >
                {options.map((o) => (
                  <option
                    disabled={
                      o.value !== time && formData.times.includes(o.value)
                    }
                    value={o.value}
                  >
                    {o.displayName}
                  </option>
                ))}
              </select>
              {formData.times.length > 1 && (
                <button
                  type="button"
                  className="btn-small btn-danger"
                  onClick={() => removeTimeSlot(index)}
                >
                  × Remove
                </button>
              )}
            </div>
          ))}

          <button
            disabled={formData.times.length === options.length}
            type="button"
            className="btn-small"
            onClick={addTimeSlot}
          >
            Add Time Slot
          </button>
        </div>

        <div className="form-group">
          <label>When a spot opens</label>
          <div className="segment-control">
            <button
              type="button"
              className={`segment-btn${formData.autoBook ? " active" : ""}`}
              onClick={() => setFormData({ ...formData, autoBook: true })}
            >
              ⚡ Book it for me
            </button>
            <button
              type="button"
              className={`segment-btn${!formData.autoBook ? " active" : ""}`}
              onClick={() => hasPhone && setFormData({ ...formData, autoBook: false })}
              disabled={!hasPhone}
            >
              💬 Just text me that my spots are open
              {!hasPhone && (
                <span className="segment-btn-hint">Add a phone number in your profile to enable this</span>
              )}
            </button>
          </div>
        </div>

        {!formData.autoBook && (
          <div className="form-group">
            <label htmlFor="minSpots">Spots needed</label>
            <small className="form-text" style={{ marginBottom: "0.5rem", display: "block" }}>Bringing a friend? Require more than 1 open spot.</small>
            <select
              id="minSpots"
              name="minSpots"
              className="select-narrow"
              value={formData.minSpots}
              onChange={onChange}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn"
            onClick={() => navigate("/dashboard")}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : "Save Appointment"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentForm;
